/**
 * Plan Builder JavaScript
 * Connects to Django pricing API and handles step-by-step product configuration
 */

// ---------------------------------------------------------------------------
// Coverage step toggle.
// Set to `true` to re-enable the Coverage step (step 5) once warranty coverage
// is ready to sell. When `false`, the builder skips Coverage entirely: the
// Coverage progress dot is hidden, Suggestions shows as step 5, and no coverage
// cost is added (treated as "No Coverage" / $0). No other changes needed.
// ---------------------------------------------------------------------------
const PB_COVERAGE_ENABLED = false;

// ---------------------------------------------------------------------------
// Activation fee toggle.
// Set to `true` to charge the $60 activation fee again. When `false`, the plan
// builder stops sending the fee (so the cart-transform Function adds $0) and
// the fee is not shown as a line property. See also activation-fee-handler.js,
// which is disabled the same way.
// ---------------------------------------------------------------------------
const PB_ACTIVATION_FEE_ENABLED = false;

class PlanBuilder {
  constructor() {
    this.apiBaseUrl = 'https://shopify-manager-grc7a7fpbee0eqdx.canadaeast-01.azurewebsites.net';
    this.pricingData = null;
    this.variations = {};
    this.currentStep = 1;
    this.maxSteps = 6;
    this.isLoading = false;
    this.isInitialized = false;
    this.selections = {
      storage: null,
      color: null,
      plan: null,
      pricing: null,
      coverage: PB_COVERAGE_ENABLED ? null : 'none',
      suggestions: []
    };
  }

  async init() {
    console.log('Initializing Plan Builder...');
    await this.loadProductData();
    await this.loadPdpPricing();
    this.isInitialized = true;
  }

  async loadPdpPricing() {
    // Fetch per-variant pricing from our pdp-pricing API (same as the PDP uses)
    try {
      const productId = window.product && window.product.id;
      if (!productId) return;
      const resp = await fetch(`${this.apiBaseUrl}/products/api/pdp-pricing/?id=${productId}`);
      if (resp.ok) {
        const data = await resp.json();
        this.pdpPricingData = data; // { variants: { variantId: {...} }, product_lowest: {...} }
        console.log('📊 PDP pricing loaded for plan builder:', data);
      }
    } catch (e) {
      console.log('⚠️ Could not load PDP pricing for plan builder:', e);
    }
  }

  async loadProductData() {
    try {
      // Get current Shopify product ID from the page
      const shopifyProductId = await this.getCurrentShopifyProductId();
      
      console.log('🔍 Current Shopify Product ID:', shopifyProductId);
      
      // Try API call with Shopify product ID filter first
      let response, data;
      
      if (shopifyProductId) {
        console.log('📡 Trying filtered API call with Shopify Product ID...');
        const params = new URLSearchParams();
        params.append('shopify_product_id', shopifyProductId);
        
        response = await fetch(`${this.apiBaseUrl}/products/api/plan-builder/products/?${params}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        data = await response.json();
        console.log('📊 Filtered API Response:', data);
        
        if (!data.products || Object.keys(data.products).length === 0) {
          throw new Error(`No products found for Shopify Product ID ${shopifyProductId} - Plan Builder unavailable for this product`);
        }
      }
      
      // Store the data and process it
      this.pricingData = data.products;
      console.log('📊 Stored pricing data:', this.pricingData);
      console.log('📊 Pricing data keys:', Object.keys(this.pricingData));
      this.processProductVariations();
      
    } catch (error) {
      console.error('Error loading product data:', error);
      throw error; // Re-throw so openPlanBuilder can handle it
    }
  }

  async getCurrentShopifyProductId() {
    let shopifyId = null;
    
    // Method 1: Try window.product (if available)
    if (window.product && window.product.id) {
      shopifyId = window.product.id;
    }
    // Method 2: Try meta tag (common Shopify pattern)
    else if (document.querySelector('meta[name="product-id"]')) {
      shopifyId = document.querySelector('meta[name="product-id"]').content;
    }
    // Method 3: Try data attribute on body or main element
    else if (document.body.dataset.productId) {
      shopifyId = document.body.dataset.productId;
    }
    // Method 4: Try script tags for product JSON
    else {
      const scripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (data.id && (data.variants || data.title)) {
            shopifyId = data.id;
            break;
          }
        } catch (e) {
          // Continue to next script
        }
      }
    }
    
    if (!shopifyId) {
      console.error('❌ Could not find Shopify ID');
      return null;
    }

    return shopifyId;
  }


  processProductVariations() {
    // Group products by storage and color to build variation matrix
    this.variations = {
      storage: new Map(),
      colors: new Map(),
      combinations: new Map()
    };

    console.log('📦 Processing filtered pricing data (should only contain current product):', this.pricingData);

    if (!this.pricingData || Object.keys(this.pricingData).length === 0) {
      console.error('❌ No product data returned from API');
      this.showProductMismatchError();
      return;
    }

    // Process all returned data (should only be current product due to API filtering)
    Object.entries(this.pricingData).forEach(([baseProductName, productGroup]) => {
      console.log('📦 Processing filtered product group:', baseProductName);
      
      if (!this.currentProductGroup) {
        this.currentProductGroup = productGroup;
        this.currentProductName = baseProductName;
      }
      
      this.processProductGroup(productGroup);
    });

    console.log('✅ Processed variations for current product:', this.variations);
    console.log('📦 Storage options:', Array.from(this.variations.storage.keys()));
    console.log('🎨 Color options:', Array.from(this.variations.colors.keys()));
  }



  processProductGroup(productGroup) {
    // Process variants from the matched product group only
    console.log('📦 Processing variants for matched product group...');
    
    Object.entries(productGroup.variants || {}).forEach(([variantKey, variant]) => {
      console.log(`  🔧 Processing variant: ${variantKey}`, variant);
      
      const storage = variant.storage;
      const color = variant.color;
      const sku = variant.sku;
      
      if (storage) {
        if (!this.variations.storage.has(storage)) {
          this.variations.storage.set(storage, {
            size: storage,
            skus: [],
            basePrice: null
          });
        }
        this.variations.storage.get(storage).skus.push(sku);
      }

      if (color) {
        if (!this.variations.colors.has(color)) {
          this.variations.colors.set(color, {
            name: color,
            skus: [],
            colorCode: this.getColorCode(color)
          });
        }
        this.variations.colors.get(color).skus.push(sku);
      }

      // Store combination
      const key = `${storage}-${color}`;
      this.variations.combinations.set(key, {
        sku,
        product: variant,
        storage: storage,
        color: color
      });
    });
  }

  showProductMismatchError() {
    const availableProducts = this.pricingData ? Object.keys(this.pricingData).join(', ') : 'None';
    const errorMessage = `
      <div class="product-mismatch-error" style="padding: 20px; text-align: center; color: #dc2626;">
        <h3>Product Not Available</h3>
        <p>Plan Builder is not available for this product yet.</p>
        <p>Available products: ${availableProducts}</p>
        <button onclick="closePlanBuilder()" class="button button--secondary">Close</button>
      </div>
    `;
    
    const storageElement = document.getElementById('storageOptions');
    if (storageElement) {
      storageElement.innerHTML = errorMessage;
    }
    this.showError('Plan Builder not available for this product');
  }

    // Add this method after processProductVariations()
  getProductImageUrl(color = null, storage = null) {
    // Use Shopify's product variants to find matching image
    if (window.product && window.product.variants) {
      // Try to find a variant that matches the selected color/storage
      const matchingVariant = window.product.variants.find(variant => {
        const variantTitle = variant.title.toLowerCase();
        const colorMatch = color ? variantTitle.includes(color.toLowerCase()) : true;
        const storageMatch = storage ? variantTitle.includes(storage.toLowerCase()) : true;
        return colorMatch && storageMatch;
      });
      
      // Prefer gang media (custom.gang_media metafield) — that's where per-color
      // phone images live; native variant.featured_image is usually empty.
      if (matchingVariant) {
        if (matchingVariant.gang_media && matchingVariant.gang_media.length && matchingVariant.gang_media[0]) {
          return matchingVariant.gang_media[0];
        }
        if (matchingVariant.featured_image) {
          return matchingVariant.featured_image.src;
        }
      }

      // If no exact match, try just color
      if (color) {
        const colorVariant = window.product.variants.find(variant =>
          variant.title.toLowerCase().includes(color.toLowerCase())
        );
        if (colorVariant) {
          if (colorVariant.gang_media && colorVariant.gang_media.length && colorVariant.gang_media[0]) {
            return colorVariant.gang_media[0];
          }
          if (colorVariant.featured_image) {
            return colorVariant.featured_image.src;
          }
        }
      }
    }
    
    // Fallback to main product image
    if (window.product && window.product.featured_image) {
      return window.product.featured_image;
    }
    
    // Final fallback - use current page image
    const currentImage = document.querySelector('.product__media img');
    if (currentImage) {
      return currentImage.src;
    }
    
    return '';
  }

  updateProductImage() {
    const imageElement = document.getElementById('selectedProductImage');
    if (imageElement) {
      let imageUrl = this.getProductImageUrl(this.selections.color, this.selections.storage);
      if (imageUrl) {
        // If it's a Shopify image URL, make sure we get high resolution
        if (imageUrl.includes('shopify') || imageUrl.includes('cdn.shopify')) {
          // Remove any existing size parameters and add our own
          imageUrl = imageUrl.split('?')[0] + '?width=240';
        }
        
        // The <img> ships with a srcset pointing at the default product image;
        // a present srcset overrides src, so clear it or the swap has no effect.
        imageElement.removeAttribute('srcset');
        imageElement.removeAttribute('sizes');
        imageElement.src = imageUrl;
        imageElement.width = 60;
        imageElement.height = 60;
        imageElement.style.display = 'block';
      }
    }
  }

  // Update your existing selectColor method
  selectColor(color) {
    this.selections.color = color;
    this.selections.pricing = null; // Reset pricing when color changes
    this.selections.plan = null; // Reset plan when color changes
    
    // Update UI
    document.querySelectorAll('.color-card').forEach(card => {
      card.classList.remove('selected');
    });
    event.target.closest('.color-card').classList.add('selected');
    
    // Update product image from Shopify variants
    this.updateProductImage();
    
    this.updateSummary();
    this.updateNavigation();
  }

  parseProductInfo(product) {
    // Parse product name to extract storage and color
    // Example: "Apple iPhone 16 - 128 GB - Black"
    const name = product.product_name;
    
    // Extract storage (look for GB pattern)
    const storageMatch = name.match(/(\d+)\s*GB/i);
    const storage = storageMatch ? `${storageMatch[1]}GB` : null;
    
    // Extract color (assume it's the last part after the last dash)
    const parts = name.split(' - ');
    const color = parts.length > 2 ? parts[parts.length - 1].trim() : null;
    
    return { storage, color };
  }

  getColorCode(colorName) {
    // Map color names to hex codes for display
    const colorMap = {
      'Black': '#1d1d1f',
      'White': '#f5f5f7',
      'Blue': '#a7c7e7',
      'Pink': '#fac5cd',
      'Red': '#ffcdd2',
      'Green': '#c8e6c9',
      'Purple': '#d1c4e9',
      'Yellow': '#fff9c4',
      'Orange': '#ff8c00',
      'Gray': '#808080',
      'Silver': '#e8e8ed',
      'Gold': '#fad5a5',
      'Rose Gold': '#e8b4b8',
      'Space Gray': '#5c5c60',
      'Midnight': '#1d1d1f',
      'Starlight': '#faf0e6',
      'Product Red': '#ba0c2f',
      'Sage': '#b2bfa8',
      'Lavender': '#c9b8d9',
      'Ultramarine': '#3f51b5'
    };
    
    return colorMap[colorName] || '#CCCCCC';
  }

  open() {
    // Always open the modal - data loading will be handled separately
    document.getElementById('planBuilderModal').style.display = 'flex';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('plan-builder-active');

    // Hide/renumber the Coverage step when it's disabled.
    this.applyCoverageVisibility();

    // Only update UI elements that don't depend on data
    this.updateProgress();
    this.updateNavigation();
  }

  // Hides the Coverage progress dot and renumbers Suggestions to "5" when
  // coverage is disabled. Safe to call repeatedly (idempotent).
  applyCoverageVisibility() {
    if (PB_COVERAGE_ENABLED) return;
    const coverageDot = document.querySelector('.progress-step[data-step="5"]');
    if (coverageDot) coverageDot.style.display = 'none';
    const suggestionsNumber = document.querySelector('.progress-step[data-step="6"] .step-number');
    if (suggestionsNumber) suggestionsNumber.textContent = '5';
  }

  close() {
    document.getElementById('planBuilderModal').style.display = 'none';
    document.body.style.overflow = '';
    document.body.classList.remove('plan-builder-active');
    this.resetSelections();
  }

  resetSelections() {
    this.currentStep = 1;
    // Must mirror the constructor's shape — omitting `suggestions`/`coverage`
    // here left them undefined on re-open, breaking the Suggestions step.
    this.selections = {
      storage: null,
      color: null,
      plan: null,
      pricing: null,
      coverage: PB_COVERAGE_ENABLED ? null : 'none',
      suggestions: []
    };
    this._srHeadsUpShown = false; // let the SmartReturn heads-up show again next open
    this.updateProgress();
    this.updateSummary();
  }

  esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Resolve the Shopify variant for the current storage+colour selection (via the
  // combination SKU). Returns the window.product variant object, or null.
  selectedVariant() {
    try {
      const key = `${this.selections.storage}-${this.selections.color}`;
      const combo = this.variations && this.variations.combinations &&
        this.variations.combinations.get(key);
      const sku = combo && combo.sku;
      if (sku && window.product && Array.isArray(window.product.variants)) {
        return window.product.variants.find(v => (v.sku || '').trim() === sku) || null;
      }
    } catch (e) {}
    return null;
  }

  // ONLINE availability of the selected variant (Shopify's variant.available).
  // Used to swap the terminal action to "Check store availability".
  isSelectedVariantSoldOutOnline() {
    const v = this.selectedVariant();
    return !!(v && v.available === false);
  }

  // True for the "With SmartReturn" opt-in options (not the "Without" ones), so the
  // info (i) only sits next to the tier the explanation is actually about.
  isSmartReturn(o) {
    return /with\s*smartreturn/i.test(o.name || '') || /smart\s*return/i.test(o.termName || '');
  }

  // Small (i) icon that opens the shared SmartReturn explanation popover. Marked
  // with data-sr-info so the document-level handler (bottom of this file) shows it.
  srInfoIconHTML() {
    return '<span class="sr-info-ic" data-sr-info role="button" tabindex="0"' +
      ' aria-label="About SmartReturn" title="About SmartReturn"' +
      ' style="display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;' +
      'margin-left:6px;border:1px solid currentColor;border-radius:50%;font-size:11px;font-style:italic;' +
      'font-family:Georgia,serif;line-height:1;opacity:.75;cursor:pointer;vertical-align:middle;">i</span>';
  }

  // Clean, dismissible heads-up shown once per open when the carried-over pricing
  // is a SmartReturn option — so customers know what they picked (with a "Full
  // details" (i) for the complete SaskTel breakdown) before they continue.
  showSmartReturnHeadsUp() {
    if (this._srHeadsUpShown) return;
    const pd = this.selections.pricingData || {};
    const isSR = this.selections.pricing &&
      this.isSmartReturn({ termName: this.selections.pricing, name: pd.display_name });
    if (!isSR) return;
    this._srHeadsUpShown = true;

    const back = document.createElement('div');
    back.className = 'pb-sr-heads';
    back.innerHTML =
      '<div class="pb-sr-heads-card" role="dialog" aria-modal="true" aria-label="SmartReturn selected">' +
        '<div class="pb-sr-heads-badge" aria-hidden="true">&#8634;</div>' +
        '<h3 class="pb-sr-heads-title">You picked SmartReturn</h3>' +
        '<p class="pb-sr-heads-text">You pay less for your phone now. At 24 months, upgrade with no fees or pay the rest to keep it.</p>' +
        '<p class="pb-sr-heads-more-row"><span class="pb-sr-heads-more" data-sr-info role="button" tabindex="0">Full details</span></p>' +
        '<button type="button" class="pb-sr-heads-ok">Got it, continue</button>' +
      '</div>';
    document.body.appendChild(back);
    const dismiss = () => back.remove();
    back.querySelector('.pb-sr-heads-ok').addEventListener('click', dismiss);
    back.addEventListener('click', (e) => { if (e.target === back) dismiss(); });
  }

  // Carry the customer's product-page choices into the builder so they don't
  // re-pick what they already selected. Reads the selected Shopify variant
  // (storage + colour, via the combination SKU) and the pricing tier chosen in
  // the PDP pricing panel (window.__jppSelection). Fully defensive: if anything
  // is missing it just leaves that selection null and the builder opens at the
  // first unmade step (original behaviour).
  prefillFromPDP() {
    try {
      // 1) Selected variant → storage + colour, matched by SKU to a combination.
      let variantId = null;
      const idInput = document.querySelector('input[name="id"]');
      if (idInput && idInput.value) variantId = String(idInput.value);
      let sku = null;
      if (variantId && window.product && Array.isArray(window.product.variants)) {
        const v = window.product.variants.find(vv => String(vv.id) === variantId);
        if (v) sku = (v.sku || '').trim();
      }
      if (sku && this.variations && this.variations.combinations) {
        for (const combo of this.variations.combinations.values()) {
          if (combo && combo.sku === sku) {
            this.selections.storage = combo.storage || null;
            this.selections.color = combo.color || null;
            break;
          }
        }
      }

      // 2) Pricing tier chosen on the PDP panel → pre-select the same option.
      const sel = window.__jppSelection;
      if (sel && sel.term && this.selections.storage && this.selections.color) {
        const key = `${this.selections.storage}-${this.selections.color}`;
        const combination = this.variations.combinations.get(key);
        const opts = combination && combination.product && combination.product.pricing_options;
        if (opts && opts[sel.term]) {
          this.selections.pricing = sel.term;
          this.selections.pricingData = opts[sel.term];
        }
      }
    } catch (e) {
      console.log('Prefill from PDP skipped:', e);
    }
  }

  // Where to open the builder: the first step the customer hasn't satisfied.
  // Everything chosen on the product page (storage/colour/pricing) carries over,
  // so when all three are set we skip straight to Plan (step 4). We only stop on
  // an earlier step when that choice wasn't made on the PDP. The footer chips +
  // SmartReturn (i) keep the carried-over pricing visible and changeable.
  firstUnsatisfiedStep() {
    if (!this.selections.storage) return 1;
    if (!this.selections.color) return 2;
    if (!this.selections.pricing) return 3;
    return 4;
  }

  // Jump straight to a step (used by the summary-card Edit chips and the
  // clickable progress tracker). Mirrors next/previousStep's coverage skip.
  goToStep(step) {
    step = Math.max(1, Math.min(this.maxSteps, step));
    if (!PB_COVERAGE_ENABLED && step === 5) step = 6;
    this.currentStep = step;
    this.renderStep(step);
    this.updateProgress();
    this.updateNavigation();
  }

  nextStep() {
    if (this.currentStep < this.maxSteps) {
      this.currentStep++;
      // Skip the Coverage step (5) while coverage is disabled.
      if (!PB_COVERAGE_ENABLED && this.currentStep === 5) this.currentStep++;
      this.renderStep(this.currentStep);
      this.updateProgress();
      this.updateNavigation();
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      // Skip the Coverage step (5) while coverage is disabled.
      if (!PB_COVERAGE_ENABLED && this.currentStep === 5) this.currentStep--;
      this.renderStep(this.currentStep);
      this.updateProgress();
      this.updateNavigation();
    }
  }

  renderStep(step) {
    console.log('renderStep called with step:', step);
    
    // Hide all steps
    document.querySelectorAll('.plan-step').forEach(el => {
      el.classList.remove('active');
      el.style.display = 'none';
      console.log('Removed active from step:', el.dataset.step);
    });

    // Show current step
    const currentStepElement = document.querySelector(`.plan-step[data-step="${step}"]`);
    console.log('Current step element:', currentStepElement);
    
    if (currentStepElement) {
      currentStepElement.classList.add('active');
      // Force display with inline style to override any CSS issues
      currentStepElement.style.display = 'block';
      console.log('Added active to step:', step);
    } else {
      console.error('Step element not found for step:', step);
    }

    // Render step content
    switch (step) {
      case 1:
        this.renderStorageOptions();
        break;
      case 2:
        this.renderColorOptions();
        break;
      case 3:
        this.renderPricingOptions();
        break;
      case 4:
        this.renderRatePlanOptions();
        break;
      case 5:
        this.renderCoverageOptions();
        break;
      case 6:
        this.renderSuggestionsOptions();
        break;
    }
  }

  renderStorageOptions() {
    console.log('renderStorageOptions called');
    const container = document.getElementById('storageOptions');
    console.log('Storage container found:', container);
    
    if (!container) {
      console.error('storageOptions container not found!');
      return;
    }
    
    container.innerHTML = '';
    
    // Add inline styles to container to ensure visibility
    container.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 12px;
      padding: 20px 0;
    `;

    console.log('Variations check:', this.variations);
    console.log('Storage check:', this.variations?.storage);

    if (!this.variations || !this.variations.storage) {
      console.log('No variations or storage found');
      container.innerHTML = '<div>No storage options available</div>';
      return;
    }

    console.log('Storage size:', this.variations.storage.size);
    console.log('Storage entries:', Array.from(this.variations.storage.entries()));

    // Sort storage options by size (128GB → 256GB → 512GB → 1TB)
    const sortedStorage = Array.from(this.variations.storage.entries()).sort((a, b) => {
      const sizeA = parseInt(a[0].replace(/\D/g, ''));
      const sizeB = parseInt(b[0].replace(/\D/g, ''));
      return sizeA - sizeB;
    });

    sortedStorage.forEach(([size, storageInfo]) => {
      console.log('Creating storage card for:', size, storageInfo);
      
      // Check if this storage size has any available variants
      console.log('🚀 About to call checkStorageAvailability for:', size);
      const isAvailable = this.checkStorageAvailability(size);
      console.log('🚀 checkStorageAvailability returned:', isAvailable, 'for size:', size);
      
      const card = document.createElement('div');
      card.className = `option-card storage-card ${this.selections.storage === size ? 'selected' : ''} ${!isAvailable ? 'out-of-stock' : ''}`;
      
      // Sold-out-ONLINE storage stays selectable so the customer can still build a
      // plan and check in-store availability (online purchase is blocked at the end).
      card.onclick = () => this.selectStorage(size);

      // Enhanced card styling for pricing layout
      const cursorStyle = 'pointer';
      card.style.cssText = `
        margin: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 14px;
        cursor: ${cursorStyle};
      `;
      
      // Format storage size with smaller GB
      const formattedSize = size.replace(/(\d+)(GB)/i, '$1<span style="font-size: 14px; font-weight: 400;">$2</span>');
      
      // Get pricing data for this storage size
      const pricingHtml = this.getStoragePricingHtml(size);
      
      card.innerHTML = `
        <div class="storage-size" style="font-size: 20px; font-weight: 700; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid rgba(0,0,0,0.1);">${formattedSize}</div>
        <div class="storage-pricing">${pricingHtml}</div>
      `;
      
      // Add out-of-stock indicator if needed
      if (!isAvailable) {
        this.addOutOfStockIndicator(card, 'Storage');
      }
      
      console.log('Appending card:', card);
      container.appendChild(card);
    });
    
    console.log('Final container HTML:', container.innerHTML);
    console.log('Container children count:', container.children.length);
  }

  getStoragePricingHtml(storageSize) {
    // Use PDP pricing API data (same source as the main product page)
    if (!this.pdpPricingData || !this.pdpPricingData.variants) {
      console.log(`❌ No PDP pricing data available for ${storageSize}`);
      return `<div style="font-size: 14px; color: #6b7280;">Pricing available</div>`;
    }

    // Find the lowest monthly_payment across all Shopify variants for this storage size
    let lowestPricing = null;
    if (window.product && window.product.variants) {
      for (const variant of window.product.variants) {
        const matchesStorage = this.variantMatchesStorage(variant, storageSize);
        if (matchesStorage) {
          const variantPricing = this.pdpPricingData.variants[variant.id];
          if (variantPricing && variantPricing.monthly_payment != null) {
            if (!lowestPricing || variantPricing.monthly_payment < lowestPricing.monthly_payment) {
              lowestPricing = variantPricing;
            }
          }
        }
      }
    }

    if (lowestPricing) {
      console.log(`✅ PDP pricing for ${storageSize}: $${lowestPricing.monthly_payment}/mo`);
      let savingsHtml = '';
      if (lowestPricing.is_on_sale && lowestPricing.savings > 0) {
        savingsHtml = `<div style="margin-top: 6px; background: linear-gradient(90deg, #253746 0%, #E44398 100%); color: white; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 4px; display: inline-block;">Save $${lowestPricing.savings.toFixed(0)}</div>`;
      }
      return `
        <div class="storage-pricing-grid" style="display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px; font-size: 11px;">
          <div style="display: flex; flex-direction: column; align-items: flex-start;">
            <div style="opacity: 0.7;">From</div>
            <div style="font-weight: 600; font-size: 13px;">$${lowestPricing.monthly_payment.toFixed(2)}/mo</div>
            <div style="opacity: 0.7;">for 24 months</div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: flex-start;">
            <div style="font-weight: 600; font-size: 13px;">$0</div>
            <div style="opacity: 0.7;">Upfront</div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: flex-start;">
            <div style="font-weight: 600; font-size: 13px;">0%</div>
            <div style="opacity: 0.7;">APR</div>
          </div>
        </div>
        ${savingsHtml}
      `;
    } else {
      console.log(`❌ No PDP pricing found for ${storageSize}`);
      return `<div style="font-size: 14px; color: #6b7280;">Pricing available</div>`;
    }
  }

  renderColorOptions() {
    const container = document.getElementById('colorOptions');
    container.innerHTML = '';

    // Build available colors from Shopify product variants (correct names like "Sage" instead of "Default")
    const availableColors = new Set();

    if (window.product && window.product.variants) {
      for (const variant of window.product.variants) {
        const matchesStorage = this.variantMatchesStorage(variant, this.selections.storage);
        if (matchesStorage) {
          // Get color from whichever option isn't the storage size
          const color = variant.option1 === this.selections.storage ? variant.option2 : variant.option1;
          if (color && color !== 'null' && color !== null && color !== 'None' && color !== 'Default Title') {
            availableColors.add(color);
          }
        }
      }
    }

    // Fallback to plan-builder API data if no Shopify variants found
    if (availableColors.size === 0) {
      this.variations.combinations.forEach((combo, key) => {
        if (combo.storage === this.selections.storage && combo.color && combo.color !== 'null' && combo.color !== null && combo.color !== 'None') {
          availableColors.add(combo.color);
        }
      });
    }

    console.log(`🎨 Available colors for ${this.selections.storage}:`, Array.from(availableColors));
    
    availableColors.forEach(colorName => {
      console.log(`🎨 Creating color card for: ${colorName}`);
      
      // Check if this color is available for the selected storage
      const isAvailable = this.checkColorAvailability(colorName, this.selections.storage);
      
      const card = document.createElement('div');
      card.className = `option-card color-card ${this.selections.color === colorName ? 'selected' : ''} ${!isAvailable ? 'out-of-stock' : ''}`;
      
      // Sold-out-ONLINE colours stay selectable so the customer can still build a
      // plan and check in-store availability (online purchase is blocked at the end).
      card.onclick = () => this.selectColor(colorName);
      
      // Get enhanced color swatch (always show, even if colorInfo is missing)
      const colorSwatch = this.getEnhancedColorSwatch(colorName);
      
      card.innerHTML = `
        <div class="color-info" style="display: flex !important; align-items: center !important; gap: 12px !important; padding: 16px !important;">
          ${colorSwatch}
          <div class="color-name" style="font-weight: 600 !important; font-size: 16px !important;">${colorName}</div>
        </div>
      `;
      
      // Add out-of-stock indicator if needed
      if (!isAvailable) {
        this.addOutOfStockIndicator(card, 'Color');
      }
      
      container.appendChild(card);
    });
    
    console.log(`🎨 Total color cards created: ${container.children.length}`);
  }

  getEnhancedColorSwatch(colorName) {
    console.log(`🎨 Getting swatch for color: "${colorName}"`);
    
    // First, try to get metafield color from window.product (same as main page)
    let metafieldColor = null;
    if (window.product && window.product.variants) {
      for (const variant of window.product.variants) {
        // Check if this variant matches the color name in any option
        if (variant.option1 === colorName || variant.option2 === colorName || variant.option3 === colorName) {
          if (variant.metafields && variant.metafields.custom && variant.metafields.custom.phone_color) {
            metafieldColor = variant.metafields.custom.phone_color;
            console.log(`🎨 Found metafield color for ${colorName}: ${metafieldColor}`);
            break;
          }
        }
      }
    }
    
    // Use metafield color if found, otherwise fall back to getColorCode
    const hexColor = metafieldColor || this.getColorCode(colorName);
    
    if (hexColor && hexColor !== '#CCCCCC') {
      // Use metafield or fallback color with proper border
      const borderColor = (colorName === 'White' || colorName === 'Starlight') ? '#d1d1d6' : hexColor;
      const colorSource = metafieldColor ? 'metafield' : 'fallback';
      console.log(`🎨 Using ${colorSource} color for ${colorName}: ${hexColor}`);
      return `<div class="enhanced-color-swatch" style="width: 40px !important; height: 40px !important; border-radius: 50% !important; background-color: ${hexColor} !important; border: 2px solid ${borderColor} !important; flex-shrink: 0 !important; box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important; display: block !important;"></div>`;
    } else {
      // No color mapping found - show color name
      console.log(`🎨 No color found for ${colorName}, showing initials`);
      return `<div class="color-name-only" style="width: 40px !important; height: 40px !important; border-radius: 50% !important; border: 2px solid #e5e5e5 !important; flex-shrink: 0 !important; display: flex !important; align-items: center !important; justify-content: center !important; font-size: 10px !important; font-weight: 600 !important; color: #666 !important; background: #f8f8f8 !important;">${colorName ? colorName.substring(0, 2) : '?'}</div>`;
    }
  }

  async renderPricingOptions() {
    const container = document.getElementById('pricingOptions');
    container.innerHTML = '<div>Loading pricing options...</div>';

    // Get the selected product combination
    const key = `${this.selections.storage}-${this.selections.color}`;
    const combination = this.variations.combinations.get(key);
    
    if (!combination) {
      container.innerHTML = '<div>No pricing options available for this combination.</div>';
      return;
    }

    const product = combination.product;
    const pricingOptions = product.pricing_options;

    container.innerHTML = '';

    // Add spacing between pricing cards
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0;
    `;

    // Group card titles (keyed by display_group coming from the pricing API).
    // Options sharing a configured group render as ONE card with sub-options.
    const GROUP_CONFIG = {
      pay_upfront: { title: 'Pay Upfront', subtitle: 'Pay for your device today — 2-year term' },
      plus_pricing: { title: 'Plus Pricing', subtitle: 'Finance your device over 24 months' }
    };

    // Build option list with display metadata (falls back to raw term name).
    let options = Object.entries(pricingOptions).map(([termName, pricing]) => ({
      termName,
      pricing,
      name: pricing.display_name || termName,
      subtitle: pricing.display_subtitle || pricing.description || '',
      group: pricing.display_group || '',
      order: (pricing.display_order != null ? pricing.display_order : 100)
    }));

    // Collapse duplicates that share the same label + group + price — e.g. New
    // and Upgrade 2YR both map to "Without SmartReturn $599.99". Keep the
    // lowest-order one; its term still drives the cart.
    options.sort((a, b) => a.order - b.order);
    const seen = new Set();
    options = options.filter(o => {
      const m = Number(o.pricing.monthly_payment) || 0;
      const priceKey = m > 0 ? `m${m}` : `u${parseFloat(o.pricing.price) || 0}`;
      const key = `${o.group}|${o.name}|${priceKey}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Split into grouped cards (only when GROUP_CONFIG knows the group) vs singles.
    const groups = {};
    const renderList = [];
    options.forEach(o => {
      if (o.group && GROUP_CONFIG[o.group]) {
        if (!groups[o.group]) {
          groups[o.group] = { key: o.group, order: o.order, options: [] };
          renderList.push({ type: 'group', ref: groups[o.group] });
        }
        groups[o.group].options.push(o);
        groups[o.group].order = Math.min(groups[o.group].order, o.order);
      } else {
        renderList.push({ type: 'single', order: o.order, option: o });
      }
    });

    // Sort by display order (groups sort by their lowest-order member).
    renderList.sort((a, b) => {
      const ao = a.type === 'group' ? a.ref.order : a.order;
      const bo = b.type === 'group' ? b.ref.order : b.order;
      return ao - bo;
    });

    renderList.forEach(entry => {
      if (entry.type === 'single') {
        container.appendChild(this.buildPricingCard(entry.option));
      } else {
        container.appendChild(this.buildPricingGroupCard(entry.ref, GROUP_CONFIG[entry.ref.key]));
      }
    });
  }

  buildPricingCard(o) {
    const pricing = o.pricing;
    const card = document.createElement('div');
    card.className = `option-card plan-card ${this.selections.pricing === o.termName ? 'selected' : ''}`;
    card.dataset.term = o.termName;
    card.onclick = () => this.selectPricing(o.termName, pricing);

    // Only show a monthly line for financed options; flat-price tiers (Retail,
    // Monthly, Prepaid, etc.) are $0/month, so we hide it.
    const monthlyValue = Number(pricing.monthly_payment) || 0;
    const monthlyText = monthlyValue > 0
      ? `<div class="plan-monthly">$${monthlyValue.toFixed(2)}/month</div>` : '';

    // Plus Pricing shows $0 upfront rather than the full device price.
    let displayPrice = pricing.price;
    if ((o.termName.includes('Plus Pricing') || o.group === 'plus_pricing') && pricing.price_source === 'calculated') {
      displayPrice = 0;
    }
    displayPrice = parseFloat(displayPrice) || 0;

    card.innerHTML = `
      <div class="plan-info">
        <h4>${o.name}${this.isSmartReturn(o) ? this.srInfoIconHTML() : ''}</h4>
        ${o.subtitle ? `<p>${o.subtitle}</p>` : ''}
      </div>
      <div class="plan-pricing">
        <div class="plan-price">$${displayPrice.toFixed(2)}</div>
        ${monthlyText}
      </div>
    `;
    return card;
  }

  buildPricingGroupCard(group, config) {
    const wrap = document.createElement('div');
    wrap.className = 'option-card plan-card plan-group-card';
    group.options.sort((a, b) => a.order - b.order);

    let html = `<div class="plan-group-header"><h4>${config.title}</h4>${config.subtitle ? `<p>${config.subtitle}</p>` : ''}</div><div class="plan-group-options">`;
    group.options.forEach((o, i) => {
      // Financed options show a monthly price; pay-today options show the upfront price.
      const monthly = Number(o.pricing.monthly_payment) || 0;
      const monthlyText = monthly > 0
        ? `$${monthly.toFixed(2)}/mo`
        : `$${(parseFloat(o.pricing.price) || 0).toFixed(2)}`;
      const selected = this.selections.pricing === o.termName ? ' selected' : '';
      html += `
        <div class="plan-suboption${selected}" data-idx="${i}">
          <div class="plan-suboption-radio"></div>
          <div class="plan-suboption-info">
            <div class="plan-suboption-name">${o.name}${this.isSmartReturn(o) ? this.srInfoIconHTML() : ''}</div>
            ${o.subtitle ? `<div class="plan-suboption-sub">${o.subtitle}</div>` : ''}
          </div>
          <div class="plan-suboption-price">${monthlyText}</div>
        </div>`;
    });
    html += '</div>';
    wrap.innerHTML = html;

    wrap.querySelectorAll('.plan-suboption').forEach(el => {
      const o = group.options[parseInt(el.dataset.idx, 10)];
      el.dataset.term = o.termName;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectPricing(o.termName, o.pricing);
      });
    });
    return wrap;
  }

  async renderRatePlanOptions() {
    const container = document.getElementById('ratePlanOptions');
    container.innerHTML = '<div>Loading rate plans...</div>';

    // Get rate plans from the product group data
    const productGroupData = this.getProductGroupData();
    
    if (!productGroupData || !productGroupData.rate_plans || !productGroupData.rate_plans.plans) {
      container.innerHTML = '<div>No rate plans available for this product.</div>';
      return;
    }

    const ratePlans = productGroupData.rate_plans.plans;
    container.innerHTML = '';

    // Add spacing between rate plan cards
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0;
    `;

    ratePlans.forEach(plan => {
      const card = document.createElement('div');
      card.className = `option-card plan-card ${this.selections.plan === plan.plan_id ? 'selected' : ''}`;
      card.onclick = () => this.selectRatePlan(plan);
      
      // Convert details to bullet points if they contain line breaks
      let formattedDetails = plan.details || `${plan.data_amount} data plan`;
      if (formattedDetails.includes('\n')) {
        // Split by line breaks and create bullet points
        const bulletPoints = formattedDetails.split('\n')
          .filter(line => line.trim()) // Remove empty lines
          .map(line => `• ${line.trim()}`)
          .join('<br>');
        formattedDetails = bulletPoints;
      }
      
      card.innerHTML = `
        <div class="plan-info">
          <h4 style="margin-bottom: 8px;">${plan.plan_name}</h4>
          <p>${formattedDetails}</p>
        </div>
        <div class="plan-pricing">
          <div class="plan-price">$${plan.monthly_price.toFixed(2)}/month</div>
          <div class="plan-monthly">${plan.data_amount}</div>
        </div>
      `;
      
      container.appendChild(card);
    });
  }

  getProductGroupData() {
    // Use the stored current product group from processProductVariations
    if (this.currentProductGroup) {
      console.log('✅ Using stored current product group for rate plans:', this.currentProductName);
      return this.currentProductGroup;
    }
    
    // Fallback: Find the product group that contains our selected combination
    console.log('⚠️ Using fallback product group lookup');
    for (const [productName, productGroup] of Object.entries(this.pricingData)) {
      if (productGroup.variants) {
        const key = `${this.selections.storage}-${this.selections.color}`;
        if (productGroup.variants[key]) {
          console.log('✅ Found product group via combination lookup:', productName);
          return productGroup;
        }
      }
    }
    
    console.error('❌ No product group found for current selections');
    return null;
  }

  renderCoverageOptions() {
    console.log('renderCoverageOptions called');
    const container = document.getElementById('coverageOptions');
    
    if (!container) {
      console.error('coverageOptions container not found!');
      return;
    }

    container.innerHTML = '';
    
    // Add spacing between coverage cards
    container.style.cssText = `
      display: grid;
      grid-template-columns: 1fr;
      gap: 12px;
      padding: 0;
    `;

    // AppleCare placeholder card
    const appleCareCard = document.createElement('div');
    appleCareCard.className = `option-card coverage-card ${this.selections.coverage === 'applecare' ? 'selected' : ''}`;
    appleCareCard.onclick = () => this.selectCoverage('applecare');
    
    appleCareCard.innerHTML = `
      <div class="coverage-info">
        <h4 style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
          <span>AppleCare+</span>
          <span style="background: #007AFF; color: white; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 500;">RECOMMENDED</span>
        </h4>
        <p style="font-size: 14px; color: #6b7280; margin-bottom: 12px;">Complete protection for your device including:</p>
        <ul style="font-size: 13px; color: #6b7280; margin: 0; padding-left: 16px; line-height: 1.4;">
          <li>Accidental damage coverage</li>
          <li>Hardware failure protection</li>
          <li>24/7 priority tech support</li>
          <li>Express replacement service</li>
        </ul>
      </div>
      <div class="coverage-pricing">
        <div class="coverage-price">$12.99/month</div>
        <div class="coverage-note" style="font-size: 12px; color: #6b7280;">Cancel anytime</div>
      </div>
    `;
    
    container.appendChild(appleCareCard);

    // No coverage option
    const noCoverageCard = document.createElement('div');
    noCoverageCard.className = `option-card coverage-card ${this.selections.coverage === 'none' ? 'selected' : ''}`;
    noCoverageCard.onclick = () => this.selectCoverage('none');
    
    noCoverageCard.innerHTML = `
      <div class="coverage-info">
        <h4 style="margin-bottom: 8px;">No Coverage</h4>
        <p style="font-size: 14px; color: #6b7280; margin-bottom: 12px;">Skip additional protection and rely on manufacturer warranty only.</p>
        <ul style="font-size: 13px; color: #6b7280; margin: 0; padding-left: 16px; line-height: 1.4;">
          <li>Standard manufacturer warranty</li>
          <li>No monthly fees</li>
          <li>Limited protection coverage</li>
        </ul>
      </div>
      <div class="coverage-pricing">
        <div class="coverage-price">$0.00</div>
        <div class="coverage-note" style="font-size: 12px; color: #6b7280;">No additional cost</div>
      </div>
    `;
    
    container.appendChild(noCoverageCard);
  }

  renderSuggestionsOptions() {
    console.log('renderSuggestionsOptions called');
    const container = document.getElementById('suggestionsOptions');
    
    if (!container) {
      console.error('suggestionsOptions container not found!');
      return;
    }

    container.innerHTML = '<div style="text-align: center; padding: 20px;">Loading recommendations...</div>';
    
    // Add spacing for suggestions grid
    container.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 12px;
      padding: 0;
    `;

    // Load Shopify recommendations
    this.loadShopifyRecommendations(container);
  }

  // Recommend accessories COMPATIBLE with the selected phone (exact device model),
  // not other phones. Uses the `suggestions-feed` collection JSON view filtered by
  // the phone's device_model metaobject via Search & Discovery. Fills to 8 with audio.
  async loadShopifyRecommendations(container) {
    try {
      const TARGET = 8;
      const dm = window.product && window.product.device_model;

      // Build the compatible_devices filter value from the phone's device model GID.
      let compatFilter = '';
      if (dm && dm.gid != null) {
        const raw = String(dm.gid);
        const fullGid = raw.indexOf('gid://') === 0 ? raw : `gid://shopify/Metaobject/${raw}`;
        compatFilter = `&filter.p.m.custom.compatible_devices=${encodeURIComponent(fullGid)}`;
      }

      // Fetch a collection through the lightweight JSON feed view (respects filters).
      const fetchFeed = async (handle, params = '') => {
        try {
          const res = await fetch(`/collections/${handle}?view=suggestions-feed${params}`);
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data.products) ? data.products : [];
        } catch (e) {
          return [];
        }
      };

      // Exact-model-compatible accessories (only if the phone has a device model).
      let cases = [], protectors = [], chargers = [];
      if (compatFilter) {
        [cases, protectors, chargers] = await Promise.all([
          fetchFeed('cases', compatFilter),
          fetchFeed('screen-protectors', compatFilter),
          fetchFeed('chargers', compatFilter)
        ]);
      }

      const items = [];
      const seen = new Set();
      const take = (list, n) => {
        let added = 0;
        for (const p of list) {
          if (items.length >= TARGET || added >= n) break;
          // Never suggest an accessory that isn't purchasable online — the feed
          // already filters these out, this is belt-and-suspenders against a
          // cached feed serving a since-sold-out item.
          if (p.available === false) continue;
          if (!seen.has(p.id)) { items.push(p); seen.add(p.id); added++; }
        }
      };

      // Mix: up to 4 cases, 1 screen protector, 1 charger (when tagged)...
      take(cases, 4);
      take(protectors, 1);
      take(chargers, 1);

      // ...then fill the rest to 8 from the Audio collection (its manual/newest order).
      if (items.length < TARGET) {
        const audio = await fetchFeed('audio');
        take(audio, TARGET - items.length);
      }

      if (items.length === 0) {
        this.renderPlaceholderSuggestions(container);
        return;
      }

      container.innerHTML = '';

      items.forEach(product => {
        const card = document.createElement('div');
        const isSelected = (this.selections.suggestions || []).find(item => item.id === product.id);
        card.className = `option-card suggestion-card ${isSelected ? 'selected' : ''}`;
        card.onclick = () => this.toggleSuggestion(product.id, {
          title: product.title,
          price: product.price,          // cents
          image: product.image,
          url: product.url,
          variant_id: product.variant_id // ready for future add-to-cart
        });

        const price = (typeof product.price === 'number') ? `$${(product.price / 100).toFixed(2)}` : 'Price varies';

        card.innerHTML = `
          <div class="suggestion-image" style="text-align: center; margin-bottom: 12px;">
            <img src="${product.image || '/assets/placeholder.png'}"
                 alt="${product.title}"
                 style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;">
          </div>
          <div class="suggestion-info">
            <h4 style="font-size: 14px; font-weight: 600; margin-bottom: 4px; line-height: 1.3;">${product.title}</h4>
            <div class="suggestion-price" style="font-size: 16px; font-weight: 600; color: #059669;">${price}</div>
          </div>
        `;

        container.appendChild(card);
      });

    } catch (error) {
      console.error('Error loading accessory suggestions:', error);
      this.renderPlaceholderSuggestions(container);
    }
  }

  renderPlaceholderSuggestions(container) {
    container.innerHTML = '';
    
    // Placeholder accessories that commonly go with phones
    const placeholders = [
      {
        id: 'case-placeholder',
        title: 'Protective Case',
        price: '$29.99',
        image: '/assets/placeholder-case.png',
        description: 'Keep your phone safe'
      },
      {
        id: 'charger-placeholder', 
        title: 'Fast Charger',
        price: '$39.99',
        image: '/assets/placeholder-charger.png',
        description: 'Charge faster'
      },
      {
        id: 'screen-protector-placeholder',
        title: 'Screen Protector',
        price: '$19.99', 
        image: '/assets/placeholder-screen.png',
        description: 'Protect your screen'
      },
      {
        id: 'wireless-charger-placeholder',
        title: 'Wireless Charger',
        price: '$49.99',
        image: '/assets/placeholder-wireless.png', 
        description: 'Convenient charging'
      }
    ];

    placeholders.forEach(item => {
      const card = document.createElement('div');
      const isSelected = this.selections.suggestions.find(suggestion => suggestion.id === item.id);
      card.className = `option-card suggestion-card ${isSelected ? 'selected' : ''}`;
      card.onclick = () => this.toggleSuggestion(item.id, item);
      
      card.innerHTML = `
        <div class="suggestion-image" style="text-align: center; margin-bottom: 12px;">
          <div style="width: 80px; height: 80px; background: #f3f4f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-size: 24px;">📱</div>
        </div>
        <div class="suggestion-info">
          <h4 style="font-size: 14px; font-weight: 600; margin-bottom: 4px; line-height: 1.3;">${item.title}</h4>
          <p style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">${item.description}</p>
          <div class="suggestion-price" style="font-size: 16px; font-weight: 600; color: #059669;">${item.price}</div>
        </div>
      `;
      
      container.appendChild(card);
    });
  }

  selectStorage(size) {
    this.selections.storage = size;
    this.selections.color = null; // Reset color when storage changes
    this.selections.pricing = null; // Reset pricing when storage changes
    this.selections.plan = null; // Reset plan when storage changes
    
    // Update UI
    document.querySelectorAll('.storage-card').forEach(card => {
      card.classList.remove('selected');
    });
    event.target.closest('.storage-card').classList.add('selected');
    
    this.updateSummary();
    this.updateNavigation();
  }


  selectPricing(termName, pricing) {
    console.log('🔍 selectPricing called with:', { termName, pricing });
    
    this.selections.pricing = termName;
    this.selections.pricingData = pricing;
    this.selections.plan = null; // Reset plan when pricing changes
    
    console.log('✅ Updated selections.pricingData:', this.selections.pricingData);

    // Update UI — highlight the element that matches the selected term, so a
    // group sub-option box lights up rather than the whole group card.
    const pricingContainer = document.getElementById('pricingOptions');
    if (pricingContainer) {
      pricingContainer.querySelectorAll('.plan-card, .plan-suboption').forEach(el => el.classList.remove('selected'));
      pricingContainer.querySelectorAll('[data-term]').forEach(el => {
        if (el.dataset.term === termName) el.classList.add('selected');
      });
    }

    this.updateSummary();
    this.updateNavigation();
  }

  selectRatePlan(plan) {
    this.selections.plan = plan.plan_id;
    this.selections.planData = plan;
    
    // Update UI
    document.querySelectorAll('.plan-card').forEach(card => {
      card.classList.remove('selected');
    });
    event.target.closest('.plan-card').classList.add('selected');
    
    this.updateSummary();
    this.updateNavigation();
  }

  selectCoverage(coverageType) {
    this.selections.coverage = coverageType;
    
    // Update UI
    document.querySelectorAll('.coverage-card').forEach(card => {
      card.classList.remove('selected');
    });
    event.target.closest('.coverage-card').classList.add('selected');
    
    this.updateSummary();
    this.updateNavigation();
  }

  toggleSuggestion(productId, productData) {
    if (!this.selections.suggestions) this.selections.suggestions = [];
    if (!this.selections.suggestions.find(item => item.id === productId)) {
      // Add to suggestions
      this.selections.suggestions.push({
        id: productId,
        ...productData
      });
    } else {
      // Remove from suggestions
      this.selections.suggestions = this.selections.suggestions.filter(item => item.id !== productId);
    }
    
    // Update UI for this specific card
    const card = event.target.closest('.suggestion-card');
    if (card) {
      card.classList.toggle('selected');
    }
    
    this.updateSummary();
    this.updateNavigation();
  }

  updateProgress() {
    document.querySelectorAll('.progress-step').forEach((step, index) => {
      const stepNumber = index + 1;
      step.classList.remove('active', 'completed');

      if (stepNumber === this.currentStep) {
        step.classList.add('active');
      } else if (stepNumber < this.currentStep) {
        step.classList.add('completed');
      }

      // Let the customer click a completed/active step to jump back and edit it
      // (wired once; guarded so we don't stack listeners on every re-render).
      const canJump = stepNumber <= this.currentStep;
      step.style.cursor = canJump ? 'pointer' : '';
      if (!step.dataset.jumpWired) {
        step.dataset.jumpWired = '1';
        step.addEventListener('click', () => {
          if (stepNumber <= this.currentStep) this.goToStep(stepNumber);
        });
      }
    });
  }

  updateSummary() {
    const productSpecs = document.getElementById('selectedProductSpecs');
    const priceTodayRow = document.getElementById('priceTodayRow');
    const priceTodayAmount = document.getElementById('priceTodayAmount');
    const monthlyTotalRow = document.getElementById('monthlyTotalRow');
    const monthlyTotalAmount = document.getElementById('monthlyTotalAmount');
    const monthlyBreakdown = document.getElementById('monthlyBreakdown');

    // Update product specs as editable chips — each jumps back to its step so a
    // customer can change a carried-over choice without re-doing everything.
    const chips = [];
    if (this.selections.storage) chips.push({ label: this.selections.storage, step: 1 });
    if (this.selections.color) chips.push({ label: this.selections.color, step: 2 });
    if (this.selections.pricing) {
      const pd = this.selections.pricingData || {};
      chips.push({ label: pd.display_name || 'Pricing', step: 3 });
    }
    if (productSpecs) {
      if (chips.length) {
        productSpecs.innerHTML = chips.map(c =>
          `<button type="button" class="pb-chip" data-step="${c.step}">` +
          `<span class="pb-chip-label">${this.esc(c.label)}</span>` +
          `<span class="pb-chip-edit">Edit</span></button>`
        ).join('');
        productSpecs.querySelectorAll('.pb-chip').forEach(btn => {
          btn.addEventListener('click', () => this.goToStep(parseInt(btn.dataset.step, 10)));
        });
      } else {
        productSpecs.textContent = '';
      }
    }

    // Gather pricing data
    let devicePrice = 0;
    let deviceMonthly = 0;
    let planMonthly = 0;

    if (this.selections.pricingData) {
      devicePrice = parseFloat(this.selections.pricingData.price) || 0;
      deviceMonthly = parseFloat(this.selections.pricingData.monthly_payment) || 0;
    }
    if (this.selections.planData) {
      planMonthly = parseFloat(this.selections.planData.monthly_price) || 0;
    }

    // Sum selected accessory suggestions (one-time, added to the "today" total)
    let accessoriesTotal = 0;
    const suggestionCount = (this.selections.suggestions || []).length;
    if (suggestionCount) {
      accessoriesTotal = this.selections.suggestions.reduce((sum, s) => {
        return sum + (typeof s.price === 'number' ? s.price : 0);
      }, 0) / 100;
    }

    // Price today row — device upfront + any selected accessories
    const todayBreakdown = document.getElementById('todayBreakdown');
    if (this.selections.pricingData || accessoriesTotal > 0) {
      const deviceToday = this.selections.pricingData ? devicePrice : 0;
      priceTodayAmount.textContent = `$${(deviceToday + accessoriesTotal).toFixed(2)}`;
      priceTodayRow.style.display = 'flex';
      if (todayBreakdown) {
        if (accessoriesTotal > 0) {
          todayBreakdown.textContent = `incl. $${accessoriesTotal.toFixed(2)} for ${suggestionCount} accessor${suggestionCount === 1 ? 'y' : 'ies'}`;
          todayBreakdown.style.display = 'block';
        } else {
          todayBreakdown.style.display = 'none';
        }
      }
    } else {
      priceTodayRow.style.display = 'none';
      if (todayBreakdown) todayBreakdown.style.display = 'none';
    }

    // Monthly total row
    const totalMonthly = deviceMonthly + planMonthly;
    if (totalMonthly > 0) {
      monthlyTotalAmount.textContent = `$${totalMonthly.toFixed(2)}`;
      monthlyTotalRow.style.display = 'flex';

      // Breakdown line
      let parts = [];
      if (deviceMonthly > 0) parts.push(`$${deviceMonthly.toFixed(2)} device`);
      if (planMonthly > 0) parts.push(`$${planMonthly.toFixed(2)} plan`);
      if (parts.length > 1) {
        monthlyBreakdown.textContent = parts.join(' + ');
        monthlyBreakdown.style.display = 'block';
      } else {
        monthlyBreakdown.style.display = 'none';
      }
    } else if (deviceMonthly > 0 || planMonthly > 0) {
      const amount = deviceMonthly || planMonthly;
      monthlyTotalAmount.textContent = `$${amount.toFixed(2)}`;
      monthlyTotalRow.style.display = 'flex';
      monthlyBreakdown.style.display = 'none';
    } else {
      monthlyTotalRow.style.display = 'none';
      monthlyBreakdown.style.display = 'none';
    }
  }

  updateNavigation() {
    const backBtn = document.querySelector('.btn-back');
    const nextBtn = document.querySelector('.btn-next');
    const addToCartBtn = document.querySelector('.btn-add-to-cart');

    // Show/hide back button
    backBtn.style.display = this.currentStep > 1 ? 'block' : 'none';

    // Show next or add to cart button
    if (this.currentStep === this.maxSteps && this.selections.plan && this.selections.coverage) {
      nextBtn.style.display = 'none';
      addToCartBtn.style.display = 'block';
      // Sold out online → the terminal action becomes "Check store availability"
      // instead of adding to cart (online purchase is blocked for sold-out devices).
      const soldOut = this.isSelectedVariantSoldOutOnline();
      const label = addToCartBtn.querySelector('.btn-text');
      if (label) label.textContent = soldOut ? 'Check store availability' : 'Add to Cart';
      addToCartBtn.classList.toggle('is-check-store', soldOut);
    } else {
      nextBtn.style.display = 'block';
      addToCartBtn.style.display = 'none';
      
      // Enable/disable next button based on selections
      const canProceed = this.canProceedToNextStep();
      nextBtn.disabled = !canProceed;
      nextBtn.style.opacity = canProceed ? '1' : '0.5';
    }
  }

  canProceedToNextStep() {
    switch (this.currentStep) {
      case 1:
        return this.selections.storage !== null;
      case 2:
        return this.selections.color !== null;
      case 3:
        return this.selections.pricing !== null;
      case 4:
        return this.selections.plan !== null;
      case 5:
        return this.selections.coverage !== null;
      case 6:
        return true; // Suggestions are optional, always allow proceeding
      default:
        return false;
    }
  }

  async addToCart() {
    if (!this.selections.plan || !this.selections.pricing || !this.selections.storage || !this.selections.color || !this.selections.coverage) {
      this.showError('Please complete all selections before adding to cart.');
      return;
    }
    // Sold out online → don't add to cart; show which stores have it instead.
    if (this.isSelectedVariantSoldOutOnline()) {
      this.checkStoreAvailability();
      return;
    }
    // Show customer type question before proceeding
    this.showCustomerTypeModal();
  }

  // Popup listing the stores that have the selected (sold-out-online) variant in
  // stock. Reuses the dataconnect-backed stock-availability API the PDP uses.
  async checkStoreAvailability() {
    const v = this.selectedVariant();
    const variantId = v && v.id;
    const phoneName = (window.product && window.product.title) || 'This device';
    const spec = [this.selections.storage, this.selections.color].filter(Boolean).join(' · ');

    const back = document.createElement('div');
    back.className = 'pb-store';
    back.innerHTML =
      '<div class="pb-store-card" role="dialog" aria-modal="true" aria-label="In-store availability">' +
        '<button type="button" class="pb-store-x" aria-label="Close">&times;</button>' +
        '<h3 class="pb-store-title">In-store availability</h3>' +
        '<p class="pb-store-sub">' + this.esc(phoneName) + (spec ? ' · ' + this.esc(spec) : '') + '</p>' +
        '<div class="pb-store-body"><p class="pb-store-loading">Checking stores…</p></div>' +
      '</div>';
    document.body.appendChild(back);
    const close = () => back.remove();
    back.querySelector('.pb-store-x').addEventListener('click', close);
    back.addEventListener('click', (e) => { if (e.target === back) close(); });
    const body = back.querySelector('.pb-store-body');

    if (!variantId) { body.innerHTML = this._storeFallbackHTML(); return; }
    try {
      const res = await fetch(`${this.apiBaseUrl}/products/api/stock-availability/?variant_id=${encodeURIComponent(variantId)}`);
      const data = res.ok ? await res.json() : {};
      const stores = (data.locations || []).filter(l => l.type === 'store' && l.qty > 0);
      if (!stores.length) { body.innerHTML = this._storeFallbackHTML(); return; }
      const qtyLabel = (q) => (q >= 10 ? '10+ available' : q + ' available');
      body.innerHTML =
        '<p class="pb-store-intro">In stock at ' + stores.length + ' store' + (stores.length === 1 ? '' : 's') + ':</p>' +
        '<ul class="pb-store-list">' +
        stores.map(loc =>
          '<li class="pb-store-item">' +
            '<div class="pb-store-item-top">' +
              '<span class="pb-store-name">' + this.esc(loc.name) + '</span>' +
              '<span class="pb-store-qty">' + this.esc(qtyLabel(loc.qty)) + '</span>' +
            '</div>' +
            (loc.address ? '<div class="pb-store-addr">' + this.esc(loc.address) + '</div>' : '') +
            (loc.phone ? '<div class="pb-store-addr">' + this.esc(loc.phone) + '</div>' : '') +
          '</li>'
        ).join('') +
        '</ul>' +
        '<a class="pb-store-cta" href="/pages/locations">See all stores &amp; hours</a>';
    } catch (e) {
      body.innerHTML = this._storeFallbackHTML();
    }
  }

  _storeFallbackHTML() {
    return '<p class="pb-store-none">This device isn’t in stock online or in our stores right now.</p>' +
           '<p class="pb-store-none-sub">Our team can help you track one down or get it ordered.</p>' +
           '<a class="pb-store-cta" href="/pages/locations">Find a store &amp; contact us</a>';
  }

  showCustomerTypeModal() {
    const modal = document.getElementById('customerTypeModal');
    if (modal) {
      modal.style.display = 'flex';
    }
  }

  handleExistingCustomer() {
    this.closeCustomerModals();
    this.proceedWithAddToCart();
  }

  handleNewCustomer() {
    const typeModal = document.getElementById('customerTypeModal');
    const newCustModal = document.getElementById('newCustomerModal');
    if (typeModal) typeModal.style.display = 'none';
    if (newCustModal) newCustModal.style.display = 'flex';
  }

  handleBackToCustomerType() {
    const newCustModal = document.getElementById('newCustomerModal');
    const typeModal = document.getElementById('customerTypeModal');
    if (newCustModal) newCustModal.style.display = 'none';
    if (typeModal) typeModal.style.display = 'flex';
  }

  closeCustomerModals() {
    const typeModal = document.getElementById('customerTypeModal');
    const newCustModal = document.getElementById('newCustomerModal');
    if (typeModal) typeModal.style.display = 'none';
    if (newCustModal) newCustModal.style.display = 'none';
  }

  async proceedWithAddToCart() {
    try {
      // Get the SKU for the selected combination
      const key = `${this.selections.storage}-${this.selections.color}`;
      const combination = this.variations.combinations.get(key);
      
      if (!combination) {
        this.showError('Selected combination not available.');
        return;
      }

      // Add line item properties for both pricing and plan
      console.log('🔍 Debug selections before cart add:', {
        pricing: this.selections.pricing,
        pricingData: this.selections.pricingData,
        storage: this.selections.storage,
        color: this.selections.color
      });
      
      const customPrice = this.selections.pricingData ? parseFloat(this.selections.pricingData.price || 0) : 0;
      
      // Fetch activation fee price and base product price from Shopify
      const activationFeeData = await this.fetchActivationFeePrice();
      const baseProductPrice = await this.fetchBaseProductPrice(combination.sku);
      
      // Calculate savings
      const deviceSavings = baseProductPrice > customPrice ? baseProductPrice - customPrice : 0;
      
      // Format Rate Plan with monthly price
      const ratePlanDisplay = this.selections.planData ? 
        `${this.selections.planData.plan_name} ($${parseFloat(this.selections.planData.monthly_price || 0).toFixed(2)}/mo)` : 
        'N/A';
      
      const properties = {
        'Pricing Option': this.selections.pricing,
        'Device Price': this.selections.pricingData ? `$${customPrice.toFixed(2)}` : 'N/A',
        'Device Monthly': this.selections.pricingData && this.selections.pricingData.monthly_payment != null ? `$${parseFloat(this.selections.pricingData.monthly_payment || 0).toFixed(2)}/mo` : 'N/A',
        'Rate Plan': ratePlanDisplay,
        'Coverage': this.selections.coverage || 'N/A',...(Date.now() < Date.parse('2026-12-08T00:00:00-06:00') && { 'FREE with your phone': 'Clear MagSafe Case and Charging Block. A Jump.ca rep will contact you to add these to your order.' }),
        // Activation fee is only shown/charged when enabled.
        ...(PB_ACTIVATION_FEE_ENABLED && { 'Activation Fee': `$${activationFeeData.price.toFixed(2)}` }),
        // Add savings information if there are savings
        ...(deviceSavings > 0 && { 'Device Savings': `Save $${deviceSavings.toFixed(2)}` }),
        // Add activation fee compare-at price if available
        ...(PB_ACTIVATION_FEE_ENABLED && activationFeeData.compareAtPrice && { 'Activation Fee Compare At': `$${activationFeeData.compareAtPrice.toFixed(2)}` }),
        // Critical: Add pricing override data for Shopify Scripts/Functions
        '_custom_price': customPrice.toString(),
        // 0 when disabled → the cart-transform Function adds no activation fee.
        '_activation_fee_price': PB_ACTIVATION_FEE_ENABLED ? activationFeeData.price.toString() : '0',
        '_base_product_price': baseProductPrice.toString(),
        '_device_savings': deviceSavings.toString(),
        '_plan_builder': 'true',
        '_pricing_source': 'plan_builder_api'
      };
      
      console.log('🏷️ Line item properties being sent:', properties);
      console.log('💰 Custom price being sent:', customPrice);

      // Find the Shopify variant ID (you'll need to map SKU to variant ID)
      const variantId = await this.getShopifyVariantId(combination.sku);
      
      if (!variantId) {
        this.showError('Product variant not found in Shopify.');
        return;
      }

      // Add to cart via Shopify AJAX API
      const cartData = {
        id: variantId,
        quantity: 1,
        properties: properties
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        const result = await response.json();

        // Add any selected accessory suggestions to the cart. Do them ONE AT A
        // TIME (not as a single atomic {items:[...]} batch): Shopify 422s the
        // WHOLE batch if any one item is unavailable, and a rapid second
        // /cart/add.js right after the phone add often hits a briefly-locked cart
        // (also 422). Individual, sequential, retried adds mean a transient lock
        // self-heals and one genuinely-unavailable accessory can't silently drop
        // the rest. Best-effort: a failure here never blocks the phone.
        const accessories = (this.selections.suggestions || []).filter(s => s.variant_id);
        for (const s of accessories) {
          await this.addAccessoryToCart(s.variant_id, s.title);
        }

        // Close the plan builder modal
        this.close();
        
        // Trigger the theme's cart system properly
        await this.triggerThemeCartUpdate(result);
        
        // Trigger activation fee handler
        document.dispatchEvent(new CustomEvent('planbuilder:added', {
          detail: {
            variant: result,
            selections: this.selections
          }
        }));
        
      } else {
        throw new Error('Failed to add to cart');
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Failed to add to cart. Please try again.');
    }
  }

  // Add a single accessory variant to the cart, retrying a 422 a couple of times.
  // A 422 immediately after the phone add is usually the transient "cart is being
  // modified" lock, which clears within a few hundred ms. A persistent 422 means
  // the variant is genuinely unavailable (e.g. out of stock) — we log and skip it
  // so it never blocks the other accessories or the phone that already went in.
  async addAccessoryToCart(variantId, title = '', attempt = 0) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (res.ok) return true;

      const err = await res.json().catch(() => ({}));
      const reason = (err.description || err.message || '').toString();
      const soldOut = /sold out|stock|unavailable/i.test(reason);

      // A 422 right after the phone add is usually a transient "cart is being
      // modified" lock, which clears in a few hundred ms — retry that. But a
      // genuine stock problem (e.g. sold out online) won't change on retry, so
      // fail it fast instead of burning three attempts.
      if (res.status === 422 && !soldOut && attempt < 2) {
        await new Promise(r => setTimeout(r, 350));
        return this.addAccessoryToCart(variantId, title, attempt + 1);
      }

      console.warn(`⚠️ Accessory not added${title ? ` (${title})` : ''} — ${res.status}: ${reason || 'unavailable'}`);
      return false;
    } catch (e) {
      console.error(`Accessory add failed${title ? ` (${title})` : ''}:`, e);
      return false;
    }
  }

  async triggerThemeCartUpdate(cartResult) {
    console.log('🛒 Triggering cart update...');
    
    try {
      // Simple approach: Just trigger standard Shopify cart events
      // This should make the theme refresh the cart content naturally
      
      // Dispatch cart refresh event (many themes listen for this)
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('cart-count, [data-cart-count]');
      cartCountElements.forEach(element => {
        if (element.textContent !== undefined) {
          const currentCount = parseInt(element.textContent) || 0;
          element.textContent = currentCount + 1;
          element.removeAttribute('hidden');
        }
      });
      
      // Open cart drawer using the theme's standard method
      const cartButton = document.querySelector('[aria-controls="CartDrawer"]');
      if (cartButton) {
        cartButton.click();
        console.log('✅ Cart drawer opened via theme button');
      }
      
    } catch (error) {
      console.log('⚠️ Cart integration error:', error);
    }
  }
  

  async fetchActivationFeePrice() {
    try {
      console.log('💰 Fetching activation fee price from Shopify...');
      const response = await fetch('/products/activation-fee.js');
      const product = await response.json();
      
      if (product && product.variants && product.variants.length > 0) {
        const variant = product.variants[0];
        const price = variant.price / 100; // Convert from cents
        const compareAtPrice = variant.compare_at_price ? variant.compare_at_price / 100 : null;
        
        console.log(`💰 Activation fee price: $${price}${compareAtPrice ? `, Compare at: $${compareAtPrice}` : ''}`);
        
        return {
          price: price,
          compareAtPrice: compareAtPrice
        };
      } else {
        console.log('⚠️ Activation fee product not found, using default');
        return {
          price: 60.00,
          compareAtPrice: null
        };
      }
    } catch (error) {
      console.log('⚠️ Could not fetch activation fee price, using default:', error);
      return {
        price: 60.00,
        compareAtPrice: null
      };
    }
  }

  async fetchBaseProductPrice(sku) {
    try {
      console.log(`💰 Fetching base product price for SKU: ${sku}`);
      
      // Try to get the current product's base price from the page
      const productData = window.product || this.currentProduct;
      if (productData && productData.variants) {
        // Find variant by SKU
        const variant = productData.variants.find(v => v.sku === sku);
        if (variant) {
          const basePrice = variant.price / 100; // Convert from cents
          console.log(`💰 Base product price: $${basePrice}`);
          return basePrice;
        }
      }
      
      // Fallback: try to fetch from current page product
      if (window.location.pathname.includes('/products/')) {
        const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
        const response = await fetch(`/products/${productHandle}.js`);
        const product = await response.json();
        
        if (product && product.variants && product.variants.length > 0) {
          // Find variant by SKU or use first variant
          const variant = product.variants.find(v => v.sku === sku) || product.variants[0];
          const basePrice = variant.price / 100;
          console.log(`💰 Base product price from product API: $${basePrice}`);
          return basePrice;
        }
      }
      
      console.log('⚠️ Could not fetch base product price, using 0');
      return 0;
    } catch (error) {
      console.log('⚠️ Error fetching base product price:', error);
      return 0;
    }
  }

  async getShopifyVariantId(sku) {
    // You'll need to implement this based on how your SKUs map to Shopify variant IDs
    // This could be a lookup in your Django API or stored in the product data
    try {
      const response = await fetch(`${this.apiBaseUrl}/products/api/sku-to-variant/${sku}/`);
      const data = await response.json();
      return data.variant_id;
    } catch (error) {
      console.error('Error fetching variant ID:', error);
      return null;
    }
  }

  showError(message) {
    // Show error message in the same area as loading indicator
    const stepContent = document.querySelector('#planBuilderModal .plan-step[data-step="1"]') || 
                       document.querySelector('#planBuilderModal .storage-section') ||
                       document.querySelector('#planBuilderModal .plan-step') ||
                       document.getElementById('storageOptions')?.parentElement;
    
    if (stepContent) {
      // Remove any existing loading indicator first
      this.hideLoadingIndicator();
      
      const errorHtml = `
        <div class="plan-builder-error" style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
        ">
          <div style="
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: #fee2e2;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;
          ">
            <svg width="24" height="24" fill="#dc2626" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM13 17h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 18px;">Plan Builder Unavailable</h3>
          <p style="margin: 0 0 16px 0; color: #6b7280; font-size: 14px; line-height: 1.4;">
            We're sorry, but the Plan Builder is not available for this product at the moment.
          </p>
          <button onclick="closePlanBuilder()" style="
            background: #3b82f6;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
          ">Close</button>
        </div>
      `;
      stepContent.style.position = 'relative';
      stepContent.insertAdjacentHTML('beforeend', errorHtml);
    } else {
      // Fallback to alert if we can't find the container
      alert('Error: ' + message);
    }
  }

  showSuccess(message) {
    // Implement success notification
    alert('Success: ' + message);
  }

  showLoadingIndicator() {
    // Show loading spinner over just the middle content area (storage selection)
    const stepContent = document.querySelector('#planBuilderModal .plan-step[data-step="1"]') || 
                       document.querySelector('#planBuilderModal .storage-section') ||
                       document.querySelector('#planBuilderModal .plan-step') ||
                       document.getElementById('storageOptions')?.parentElement;
    
    if (stepContent) {
      const loadingHtml = `
        <div class="plan-builder-loading" style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          border-radius: 8px;
        ">
          <div class="loading-spinner" style="
            width: 40px;
            height: 40px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          "></div>
          <div style="font-size: 16px; color: #666;">Loading Plan Builder...</div>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;
      stepContent.style.position = 'relative'; // Ensure relative positioning for absolute overlay
      stepContent.insertAdjacentHTML('beforeend', loadingHtml);
    }
  }

  hideLoadingIndicator() {
    const loadingElement = document.querySelector('.plan-builder-loading');
    if (loadingElement) {
      loadingElement.remove();
    }
    
    // Also remove any error messages
    const errorElement = document.querySelector('.plan-builder-error');
    if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * A product may encode storage as a variant option (e.g. option1 "256GB"), or
   * — for a single-storage product whose Shopify variants are colour-only (a
   * pre-launch phone that hasn't been split into storage variants yet) — not at
   * all. Detect that once so storage matching still works in the colour-only case.
   */
  productEncodesStorage() {
    const variants = (window.product && window.product.variants) || [];
    if (!variants.length) return false; // unknown → don't cache a wrong answer
    if (this._encodesStorage != null) return this._encodesStorage;
    const re = /^\s*\d+\s?(GB|TB)\s*$/i;
    this._encodesStorage = variants.some(v =>
      re.test(v.option1 || '') || re.test(v.option2 || '') || re.test(v.option3 || '')
    );
    return this._encodesStorage;
  }

  /**
   * True if a Shopify variant belongs to the given storage size. Matches on the
   * variant options normally; for a single-storage (colour-only) product every
   * variant belongs to the one storage size the builder offers.
   */
  variantMatchesStorage(variant, storageSize) {
    if (variant.option1 === storageSize || variant.option2 === storageSize || variant.option3 === storageSize) {
      return true;
    }
    return !this.productEncodesStorage();
  }

  /**
   * Check if a storage size has any available variants
   */
  checkStorageAvailability(storageSize) {
    console.log(`🔍 Checking storage availability for: ${storageSize}`);
    
    // OPTIMIZATION: Use window.product.variants for stock data (same as metafield colors)
    // This uses Shopify's real-time stock data instead of API data
    if (window.product && window.product.variants) {
      for (const variant of window.product.variants) {
        // Check if this variant matches the storage size (or belongs to a
        // single-storage, colour-only product)
        if (this.variantMatchesStorage(variant, storageSize)) {
          if (variant.available) {
            console.log(`📦 Found available ${storageSize} variant from Shopify: ${variant.title}`);
            return true; // At least one variant of this storage size is available
          }
        }
      }
      console.log(`❌ No available Shopify variants found for storage size: ${storageSize}`);
      return false;
    }
    
    // Fallback to API data if window.product not available
    if (!this.pricingData) {
      console.log(`⚠️ No Shopify or pricing data available - assuming ${storageSize} is available`);
      return true;
    }
    
    // Check if any variant with this storage size is available
    for (const [productName, productGroup] of Object.entries(this.pricingData)) {
      if (productGroup.variants) {
        for (const [variantKey, variant] of Object.entries(productGroup.variants)) {
          if (variant.storage === storageSize) {
            // Check if this variant is available according to API
            const isAvailable = variant.available !== false; // Default to true if not specified
            console.log(`📦 Found ${storageSize} variant ${variantKey}: available = ${isAvailable}`);
            
            if (isAvailable) {
              return true; // At least one variant of this storage size is available
            }
          }
        }
      }
    }
    
    console.log(`❌ No available variants found for storage size: ${storageSize}`);
    return false; // No available variants found for this storage size
    
    // Try to get Shopify product data
    let shopifyVariants = null;
    
    if (window.product && window.product.variants) {
      shopifyVariants = window.product.variants;
      console.log(`✅ Using window.product variants (${shopifyVariants.length} variants)`);
    } else {
      // Try multiple methods to get product data from the page
      console.log(`🔍 Trying to find product data from page scripts...`);
      
      // Method 1: Look for data-product-json attribute
      let productScript = document.querySelector('script[type="application/json"][data-product-json]');
      if (!productScript) {
        // Method 2: Look for any script containing product data
        const allScripts = document.querySelectorAll('script[type="application/json"]');
        console.log(`🔍 Found ${allScripts.length} JSON scripts to check`);
        
        for (let i = 0; i < allScripts.length; i++) {
          const script = allScripts[i];
          try {
            const data = JSON.parse(script.textContent);
            console.log(`🔍 Script ${i + 1} contains:`, Object.keys(data));
            
            if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
              productScript = script;
              console.log(`✅ Found product data in script ${i + 1} with ${data.variants.length} variants`);
              console.log(`🔍 Sample variant:`, data.variants[0]);
              break;
            } else if (data.product && data.product.variants) {
              // Sometimes it's nested under 'product'
              productScript = script;
              console.log(`✅ Found nested product data in script ${i + 1} with ${data.product.variants.length} variants`);
              console.log(`🔍 Sample variant:`, data.product.variants[0]);
              break;
            } else if (data.id && data.available !== undefined && data.option1 !== undefined) {
              // This might be a single variant object - we need to find the product with all variants
              console.log(`🔍 Found single variant in script ${i + 1}:`, {
                id: data.id,
                title: data.title,
                available: data.available,
                option1: data.option1,
                option2: data.option2,
                option3: data.option3
              });
              // Don't break here - keep looking for the full product with all variants
            }
          } catch (e) {
            console.log(`❌ Script ${i + 1} failed to parse:`, e.message);
          }
        }
      }
      
      if (productScript) {
        try {
          const productData = JSON.parse(productScript.textContent);
          if (productData.variants) {
            shopifyVariants = productData.variants;
            console.log(`✅ Using script tag variants (${shopifyVariants.length} variants)`);
            console.log(`🔍 First variant example:`, shopifyVariants[0]);
          } else if (productData.product && productData.product.variants) {
            shopifyVariants = productData.product.variants;
            console.log(`✅ Using nested script tag variants (${shopifyVariants.length} variants)`);
            console.log(`🔍 First variant example:`, shopifyVariants[0]);
          }
        } catch (e) {
          console.log(`❌ Failed to parse product script:`, e);
        }
      } else {
        console.log(`❌ No product scripts found on page`);
      }
    }
    
    if (!shopifyVariants) {
      // Last resort: Try to get product handle from URL and fetch via Shopify API
      const pathParts = window.location.pathname.split('/');
      const productHandle = pathParts[pathParts.length - 1];
      
      if (productHandle && productHandle !== 'products') {
        console.log(`🔍 Trying Shopify API for product handle: ${productHandle}`);
        
        // Try synchronous fetch (this might not work due to CORS, but worth trying)
        try {
          const response = fetch(`/products/${productHandle}.js`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          // Since we can't use async here, we'll have to assume available for now
          console.log(`⚠️ Cannot use async fetch in availability check, assuming ${storageSize} is available`);
          return true;
        } catch (e) {
          console.log(`❌ Shopify API fetch failed:`, e);
        }
      }
      
      console.log(`⚠️ No Shopify variant data available, assuming ${storageSize} is available`);
      return true; // Assume available if we can't check
    }

    console.log(`🔍 Total variants available:`, shopifyVariants.length);
    console.log(`🔍 First few variants:`, shopifyVariants.slice(0, 3).map(v => ({
      id: v.id,
      title: v.title,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      available: v.available
    })));

    // Find variants that match this storage size
    const matchingVariants = shopifyVariants.filter(variant => {
      const title = variant.title || '';
      const matchesTitle = title.includes(storageSize);
      const matchesOption1 = variant.option1 === storageSize;
      const matchesOption2 = variant.option2 === storageSize;
      const matchesOption3 = variant.option3 === storageSize;
      
      const matches = matchesTitle || matchesOption1 || matchesOption2 || matchesOption3;
      
      if (matches) {
        console.log(`✅ Found matching variant for ${storageSize}:`, {
          title: variant.title,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
          available: variant.available
        });
      }
      
      return matches;
    });

    // Check if any matching variant is available
    const hasAvailable = matchingVariants.some(variant => variant.available);
    
    console.log(`📦 Storage ${storageSize} - Found ${matchingVariants.length} matching variants, ${matchingVariants.filter(v => v.available).length} available`);
    console.log(`📦 Final availability result for ${storageSize}:`, hasAvailable);
    
    return hasAvailable;
  }

  /**
   * Check if a color is available for the given storage size
   */
  checkColorAvailability(colorName, storageSize) {
    console.log(`🔍 Checking color availability for: ${colorName} + ${storageSize}`);
    
    // OPTIMIZATION: Use window.product.variants for stock data (same as metafield colors)
    // This uses Shopify's real-time stock data instead of API data
    if (window.product && window.product.variants) {
      for (const variant of window.product.variants) {
        // Check if this variant matches both storage and color in the options
        const matchesStorage = this.variantMatchesStorage(variant, storageSize);
        const matchesColor = variant.option1 === colorName || variant.option2 === colorName || variant.option3 === colorName;
        
        if (matchesStorage && matchesColor) {
          console.log(`🎨 Found ${storageSize}-${colorName} variant from Shopify: ${variant.title}, available = ${variant.available}`);
          return variant.available;
        }
      }
      console.log(`❌ No Shopify variant found for ${storageSize}-${colorName} combination`);
      return false;
    }
    
    // Fallback to API data if window.product not available
    if (!this.pricingData) {
      console.log(`⚠️ No Shopify or pricing data available - assuming ${colorName} is available`);
      return true;
    }
    
    // Check if the specific storage/color combination is available
    for (const [productName, productGroup] of Object.entries(this.pricingData)) {
      if (productGroup.variants) {
        for (const [variantKey, variant] of Object.entries(productGroup.variants)) {
          if (variant.storage === storageSize && variant.color === colorName) {
            // Check if this specific variant is available according to API
            const isAvailable = variant.available !== false; // Default to true if not specified
            console.log(`🎨 Found ${storageSize}-${colorName} variant: available = ${isAvailable}`);
            return isAvailable;
          }
        }
      }
    }
    
    console.log(`❌ No variant found for ${storageSize}-${colorName} combination`);
    return false; // Specific combination not found
    
    // Try to get Shopify product data
    let shopifyVariants = null;
    
    if (window.product && window.product.variants) {
      shopifyVariants = window.product.variants;
      console.log(`✅ Using window.product variants for color check (${shopifyVariants.length} variants)`);
    } else {
      // Try multiple methods to get product data from the page
      console.log(`🔍 Trying to find product data from page scripts for color check...`);
      
      // Method 1: Look for data-product-json attribute
      let productScript = document.querySelector('script[type="application/json"][data-product-json]');
      if (!productScript) {
        // Method 2: Look for any script containing product data
        const allScripts = document.querySelectorAll('script[type="application/json"]');
        console.log(`🔍 Found ${allScripts.length} JSON scripts to check for colors`);
        
        for (let i = 0; i < allScripts.length; i++) {
          const script = allScripts[i];
          try {
            const data = JSON.parse(script.textContent);
            console.log(`🔍 Color check - Script ${i + 1} contains:`, Object.keys(data));
            
            if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
              productScript = script;
              console.log(`✅ Found product data for colors in script ${i + 1} with ${data.variants.length} variants`);
              break;
            } else if (data.product && data.product.variants) {
              productScript = script;
              console.log(`✅ Found nested product data for colors in script ${i + 1} with ${data.product.variants.length} variants`);
              break;
            }
          } catch (e) {
            console.log(`❌ Color check - Script ${i + 1} failed to parse:`, e.message);
          }
        }
      }
      
      if (productScript) {
        try {
          const productData = JSON.parse(productScript.textContent);
          if (productData.variants) {
            shopifyVariants = productData.variants;
            console.log(`✅ Using script tag variants for color check (${shopifyVariants.length} variants)`);
          } else if (productData.product && productData.product.variants) {
            shopifyVariants = productData.product.variants;
            console.log(`✅ Using nested script tag variants for color check (${shopifyVariants.length} variants)`);
          }
        } catch (e) {
          console.log(`❌ Failed to parse product script for color check:`, e);
        }
      } else {
        console.log(`❌ No product scripts found on page for color check`);
      }
    }
    
    if (!shopifyVariants) {
      console.log(`⚠️ No Shopify variant data available, assuming ${colorName} is available`);
      return true; // Assume available if we can't check
    }

    // Find variants that match both storage and color
    const matchingVariants = shopifyVariants.filter(variant => {
      const title = variant.title || '';
      const hasStorage = title.includes(storageSize) || 
                        variant.option1 === storageSize || 
                        variant.option2 === storageSize || 
                        variant.option3 === storageSize;
      const hasColor = title.includes(colorName) || 
                      variant.option1 === colorName || 
                      variant.option2 === colorName || 
                      variant.option3 === colorName;
      
      const matches = hasStorage && hasColor;
      
      if (matches) {
        console.log(`✅ Found matching variant for ${colorName} + ${storageSize}:`, {
          title: variant.title,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
          available: variant.available
        });
      }
      
      return matches;
    });

    // Check if any matching variant is available
    const hasAvailable = matchingVariants.some(variant => variant.available);
    
    console.log(`🎨 Color ${colorName} + Storage ${storageSize} - Found ${matchingVariants.length} matching variants, ${matchingVariants.filter(v => v.available).length} available`);
    console.log(`🎨 Final availability result for ${colorName} + ${storageSize}:`, hasAvailable);
    
    return hasAvailable;
  }

  /**
   * Add out-of-stock indicator to a card element
   */
  addOutOfStockIndicator(card, type = 'variant') {
    // Dim it to signal sold-out-online, but keep it SELECTABLE — the customer can
    // still build a plan and check in-store availability for a sold-out device.
    card.style.opacity = '0.6';
    card.style.position = 'relative';
    card.style.filter = 'grayscale(40%)';

    // Create "sold out online" badge
    const badge = document.createElement('div');
    badge.className = 'plan-builder-oos-badge';
    badge.textContent = 'Sold out online';
    badge.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background: #dc2626;
      color: white;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 8px;
      border-radius: 4px;
      z-index: 10;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;
    
    card.appendChild(badge);

    // Still selectable — sold out online only blocks the online purchase, not the plan.
    card.style.cursor = 'pointer';
    card.title = `${type} is sold out online — you can still build a plan and check in-store availability`;

    console.log(`📍 Added sold-out-online indicator to ${type} card`);
  }

  /**
   * Show message when user tries to select out-of-stock variant
   */
  showOutOfStockMessage(type, value) {
    console.log(`⚠️ User tried to select out-of-stock ${type}: ${value}`);
    
    // Create temporary message
    const message = document.createElement('div');
    message.className = 'plan-builder-oos-message';
    message.textContent = `${value} ${type.toLowerCase()} is currently out of stock`;
    message.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fee2e2;
      border: 1px solid #fecaca;
      color: #dc2626;
      padding: 12px 20px;
      border-radius: 8px;
      font-weight: 600;
      z-index: 1000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: fadeInOut 2s ease-in-out;
    `;
    
    // Add animation keyframes
    if (!document.querySelector('#oos-animation-styles')) {
      const style = document.createElement('style');
      style.id = 'oos-animation-styles';
      style.textContent = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
          20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(message);
    
    // Remove message after animation
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 2000);
  }
}

// Global functions for the modal
let planBuilder = null;

// Initialize Plan Builder on page load to avoid double-click issues
function initializePlanBuilder() {
  if (!planBuilder) {
    planBuilder = new PlanBuilder();
    console.log('Plan Builder instance created and ready');
  }
}

async function openPlanBuilder() {
  // Ensure Plan Builder exists (fallback if DOM event didn't fire)
  if (!planBuilder) {
    planBuilder = new PlanBuilder();
  }
  
  // Prevent multiple clicks while loading
  if (planBuilder.isLoading) {
    console.log('Plan Builder already loading, please wait...');
    return;
  }
  
  // Set loading state immediately
  planBuilder.isLoading = true;
  
  try {
    // Open modal first
    planBuilder.open();
    
    // Show loading indicator inside modal
    planBuilder.showLoadingIndicator();
    
    // Initialize the plan builder data (only if not already initialized)
    if (!planBuilder.isInitialized) {
      await planBuilder.init();
      planBuilder.hideLoadingIndicator();
    } else {
      // Data already loaded, just show it
      planBuilder.hideLoadingIndicator();
    }
    // Carry over what the customer already chose on the product page, then open
    // at the first step they haven't made (usually straight to Pricing). If they
    // chose nothing, prefill leaves everything null and we open at Storage.
    planBuilder.prefillFromPDP();
    planBuilder.goToStep(planBuilder.firstUnsatisfiedStep());
    planBuilder.updateSummary();   // reflect the carried-over picks in the footer card
    planBuilder.showSmartReturnHeadsUp();  // gentle heads-up if SmartReturn was carried in

    planBuilder.isLoading = false;
    
  } catch (error) {
    console.error('Error initializing Plan Builder:', error);
    planBuilder.hideLoadingIndicator();
    planBuilder.isLoading = false;
    planBuilder.showError('Failed to load Plan Builder. Please try again.');
  }
}

function closePlanBuilder() {
  if (planBuilder) {
    planBuilder.close();
  }
}

function nextStep() {
  if (planBuilder) {
    planBuilder.nextStep();
  }
}

function previousStep() {
  if (planBuilder) {
    planBuilder.previousStep();
  }
}

function addToCart() {
  if (planBuilder) {
    planBuilder.addToCart();
  }
}

console.log('Plan Builder script loaded');

// Initialize Plan Builder based on DOM state
if (document.readyState === 'loading') {
  // DOM is still loading, wait for DOMContentLoaded
  document.addEventListener('DOMContentLoaded', initializePlanBuilder);
} else {
  // DOM already loaded, create immediately
  initializePlanBuilder();
}

// ---------------------------------------------------------------------------
// Shared "What is SmartReturn?" popover + Edit-chip styles.
// One document-level handler covers every [data-sr-info] (i) icon — both the
// builder's pricing step AND the product-page pricing panel — so customers get
// the SaskTel explanation anywhere SmartReturn is offered. Capture phase +
// stopPropagation keeps the (i) click from also selecting the pricing tier.
// ---------------------------------------------------------------------------
(function initSmartReturnInfo() {
  if (window.__srInfoInit) return;
  window.__srInfoInit = true;

  var BLURB = "SmartReturn offers an additional discount that reduces the cost of the phone at the time of purchase. During the return window, you'll have the flexibility to upgrade your phone without upgrade fees, or you can payback the SmartReturn discount and keep the phone.";

  // Edit-chip + (i) hover styling, injected once.
  try {
    var st = document.createElement('style');
    st.textContent =
      '.pb-chip{display:inline-flex;align-items:center;gap:6px;margin:2px 6px 2px 0;padding:3px 8px;' +
      'border:1px solid #d1d5db;border-radius:9999px;background:#fff;font-size:12px;color:#374151;cursor:pointer;}' +
      '.pb-chip:hover{border-color:#00a98e;}' +
      '.pb-chip-edit{font-size:10px;font-weight:600;color:#00a98e;text-transform:uppercase;letter-spacing:.03em;}' +
      '.sr-info-ic:hover,.jpp-info:hover{opacity:1 !important;}' +
      '.sr-info-popover{animation:srPopIn .12s ease-out;}' +
      '@keyframes srPopIn{from{opacity:0;transform:translateY(-4px);}to{opacity:1;transform:none;}}' +
      // SmartReturn "heads up" card shown on open when SmartReturn is pre-selected.
      '.pb-sr-heads{position:fixed;inset:0;z-index:1000001;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(17,24,39,.45);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);padding:20px;animation:srFade .15s ease-out;}' +
      '.pb-sr-heads-card{background:#fff;border-radius:16px;max-width:380px;width:100%;padding:24px;text-align:center;' +
      'box-shadow:0 20px 50px rgba(0,0,0,.28);animation:srPopIn .18s ease-out;}' +
      '.pb-sr-heads-badge{width:44px;height:44px;border-radius:50%;background:#ecfdf5;color:#00a98e;display:flex;' +
      'align-items:center;justify-content:center;font-size:22px;margin:0 auto 12px;}' +
      '.pb-sr-heads-title{margin:0 0 8px;font-size:18px;font-weight:700;color:#111827;}' +
      '.pb-sr-heads-text{margin:0 0 10px;font-size:14px;line-height:1.5;color:#4b5563;}' +
      '.pb-sr-heads-more-row{margin:0 0 20px;}' +
      '.pb-sr-heads-more{color:#00a98e;font-weight:600;cursor:pointer;white-space:nowrap;}' +
      '.pb-sr-heads-more:hover{text-decoration:underline;}' +
      '.pb-sr-heads-ok{background:#00a98e;color:#fff;border:none;border-radius:9999px;padding:12px 28px;' +
      'font-size:15px;font-weight:600;cursor:pointer;width:100%;}' +
      '.pb-sr-heads-ok:hover{background:#00917a;}' +
      // "Check store availability" popup (sold-out-online devices).
      '.pb-store{position:fixed;inset:0;z-index:1000001;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(17,24,39,.45);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);padding:20px;animation:srFade .15s ease-out;}' +
      '.pb-store-card{position:relative;background:#fff;border-radius:16px;max-width:420px;width:100%;padding:24px;' +
      'max-height:80vh;overflow-y:auto;box-shadow:0 20px 50px rgba(0,0,0,.28);animation:srPopIn .18s ease-out;}' +
      '.pb-store-x{position:absolute;top:14px;right:16px;border:none;background:none;font-size:24px;line-height:1;color:#9ca3af;cursor:pointer;}' +
      '.pb-store-title{margin:0 0 4px;font-size:18px;font-weight:700;color:#111827;}' +
      '.pb-store-sub{margin:0 0 16px;font-size:13px;color:#6b7280;}' +
      '.pb-store-loading{font-size:14px;color:#6b7280;}' +
      '.pb-store-intro{margin:0 0 12px;font-size:14px;font-weight:600;color:#111827;}' +
      '.pb-store-list{list-style:none;margin:0 0 16px;padding:0;display:flex;flex-direction:column;gap:10px;}' +
      '.pb-store-item{border:1px solid #e5e7eb;border-radius:10px;padding:12px 14px;}' +
      '.pb-store-item-top{display:flex;justify-content:space-between;align-items:baseline;gap:10px;}' +
      '.pb-store-name{font-weight:600;font-size:14px;color:#111827;}' +
      '.pb-store-qty{font-size:12px;font-weight:600;color:#00a98e;white-space:nowrap;}' +
      '.pb-store-addr{font-size:12px;color:#6b7280;margin-top:4px;line-height:1.4;}' +
      '.pb-store-none{margin:8px 0 6px;font-size:14px;font-weight:600;color:#111827;}' +
      '.pb-store-none-sub{margin:0 0 16px;font-size:13px;color:#6b7280;line-height:1.5;}' +
      '.pb-store-cta{display:inline-block;background:#00a98e;color:#fff;border-radius:9999px;padding:11px 22px;' +
      'font-size:14px;font-weight:600;text-decoration:none;}' +
      '.pb-store-cta:hover{background:#00917a;}' +
      '@keyframes srFade{from{opacity:0}to{opacity:1}}';
    document.head.appendChild(st);
  } catch (e) {}

  var pop = null;
  function closePop() {
    if (pop) { pop.remove(); pop = null; }
    document.removeEventListener('click', onOutside, true);
  }
  function onOutside(e) {
    if (pop && !pop.contains(e.target) && !e.target.closest('[data-sr-info]')) closePop();
  }
  function showPop(anchor) {
    closePop();
    pop = document.createElement('div');
    pop.className = 'sr-info-popover';
    pop.setAttribute('role', 'tooltip');
    pop.textContent = BLURB;
    Object.assign(pop.style, {
      // Above the builder modal (z-index 999999/1000000) and the heads-up overlay,
      // so the (i) popover works inside the builder too, not just on the PDP.
      position: 'absolute', zIndex: '2147483647', maxWidth: '300px',
      background: '#1a1a1a', color: '#fff', fontSize: '13px', lineHeight: '1.45',
      padding: '12px 14px', borderRadius: '8px', boxShadow: '0 6px 24px rgba(0,0,0,.28)'
    });
    document.body.appendChild(pop);
    var r = anchor.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    var top = window.scrollY + r.bottom + 8;
    var left = window.scrollX + r.left;
    left = Math.min(left, window.scrollX + vw - pop.offsetWidth - 12);
    left = Math.max(left, window.scrollX + 12);
    pop.style.top = top + 'px';
    pop.style.left = left + 'px';
    setTimeout(function () { document.addEventListener('click', onOutside, true); }, 0);
  }

  document.addEventListener('click', function (e) {
    var info = e.target.closest && e.target.closest('[data-sr-info]');
    if (!info) return;
    e.stopPropagation();
    e.preventDefault();
    if (pop) { closePop(); return; } // toggle off if already open
    showPop(info);
  }, true);

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePop(); });
})();
